import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DonorCombobox } from '@/components/DonorCombobox'
import { TeamPicker, useTeamMemberships, defaultTeamId } from '@/components/TeamPicker'
import { sheetFetch } from '@/lib/sheetFetch'
import { DuplicateWarningDialog } from './DuplicateWarningDialog'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import type CrmFundraisingPaymentMethod from '@/types/serializers/CrmFundraisingPaymentMethod'
import type CrmFundraisingDonationNature from '@/types/serializers/CrmFundraisingDonationNature'

// Mirrors PaymentMethod::REFERENCE_REQUIRED_CODES (server-side source of truth).
// Methods whose donations must carry a transaction reference.
const REFERENCE_REQUIRED_CODES = ['cheque', 'virement_bancaire']

interface PrefillDonor {
  id: number
  type: string
  name: string
}

interface CreateDonationSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaigns: CrmFundraisingCampaign[]
  paymentMethods: CrmFundraisingPaymentMethod[]
  natures: CrmFundraisingDonationNature[]
  defaultOpportunityId?: number
  /**
   * Pre-fills the donor selection when present. Used by the prefill deep-link
   * from DonorDetailSheet ("Nouveau don" CTA). When set together with
   * `lockDonor: true`, the donor picker renders read-only.
   */
  defaultDonor?: PrefillDonor
  lockDonor?: boolean
}

interface DonationFormData {
  donation: {
    donor_type: string
    donor_id: string
    campaign_id: string
    payment_method_id: string
    nature_id: string
    amount: string
    donation_date: string
    opportunity_id: string
    transaction_reference: string
    notes: string
  }
  // Owning-team choice (US3) — top-level sibling param, not nested in
  // `donation`. Resolved against the actor's direct memberships server-side.
  team_id: string
}

interface DuplicateMatch {
  id: number
  amount_formatted: string
  date: string
  status: string
  campaign_name: string | null
}

function defaultFormData(opportunityId?: number, teamId = '', campaignId = ''): DonationFormData {
  return {
    donation: {
      donor_type: '',
      donor_id: '',
      campaign_id: campaignId,
      payment_method_id: '',
      nature_id: '',
      amount: '',
      donation_date: new Date().toISOString().split('T')[0],
      opportunity_id: opportunityId ? String(opportunityId) : '',
      transaction_reference: '',
      notes: '',
    },
    team_id: teamId,
  }
}

export function CreateDonationSheet({
  open,
  onOpenChange,
  campaigns,
  paymentMethods,
  natures,
  defaultOpportunityId,
  defaultDonor,
  lockDonor = false,
}: CreateDonationSheetProps) {
  const noActiveNatures = natures.length === 0
  const memberships = useTeamMemberships()
  // Pre-select the "Hors campagne" system campaign (US6) so a donation can be
  // recorded without choosing one. Resolved by the `system` flag, never by name.
  // Falls back to no pre-selection if (unexpectedly) absent from the prop.
  const systemCampaignId = campaigns.find((c) => c.system)?.id
  const systemCampaignValue = systemCampaignId ? String(systemCampaignId) : ''
  const initialFormData = (): DonationFormData => {
    const data = defaultFormData(
      defaultOpportunityId,
      defaultTeamId(memberships),
      systemCampaignValue,
    )
    if (defaultDonor) {
      data.donation.donor_type = defaultDonor.type
      data.donation.donor_id = String(defaultDonor.id)
    }
    return data
  }

  // Local form state + a flat `errors` map keyed on the server's attribute
  // names (R1/KTD3). The donation create flow submits through sheetFetch
  // (not Inertia's useForm) because useForm cannot read validation errors
  // returned as a 422 body — only from redirect-delivered page props, which
  // is exactly what caused the original silent failure.
  const [formData, setFormData] = useState<DonationFormData>(initialFormData)
  const [selectedDonor, setSelectedDonor] = useState<{ id: number; type: string; name: string } | null>(
    defaultDonor ?? null
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)

  // Duplicate-detection feedback (R2) is a DISTINCT outcome from validation
  // errors: the server returns it as a 200 body with a `duplicates` key, fed
  // here straight into the dialog (no Inertia props round-trip).
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([])
  const [showDuplicates, setShowDuplicates] = useState(false)
  const [pendingDonation, setPendingDonation] = useState<Record<string, unknown>>({})
  const [pendingTeamId, setPendingTeamId] = useState('')

  function updateDonation(patch: Partial<DonationFormData['donation']>) {
    setFormData((prev) => ({ ...prev, donation: { ...prev.donation, ...patch } }))
  }

  // Reference is required only for chèque/virement. Recomputed each render off
  // the selected method's code, so the marker updates live on selection change.
  const selectedPaymentMethod = paymentMethods.find(
    (pm) => String(pm.id) === formData.donation.payment_method_id,
  )
  const referenceRequired = selectedPaymentMethod
    ? REFERENCE_REQUIRED_CODES.includes(selectedPaymentMethod.code)
    : false

  function handleDonorChange(donor: { id: number; type: string; name: string } | null) {
    setSelectedDonor(donor)
    updateDonation({
      donor_type: donor?.type ?? '',
      donor_id: donor ? String(donor.id) : '',
    })
  }

  function resetForm() {
    setFormData(initialFormData())
    setSelectedDonor(defaultDonor ?? null)
    setErrors({})
  }

  function handleClose() {
    resetForm()
    onOpenChange(false)
  }

  // 201: clear the sheet and refresh the index list in place (R3). Unlike the
  // old Inertia flow, success no longer navigates to the donation Show page.
  function handleCreated() {
    resetForm()
    onOpenChange(false)
    router.reload({ only: [ 'donations', 'pagy' ] })
  }

  async function handleResponse(response: Response) {
    if (response.status === 201) {
      handleCreated()
      return
    }
    if (response.status === 422) {
      const json = (await response.json()) as { errors?: Record<string, string> }
      setErrors(json.errors ?? {})
      return
    }
    if (response.status === 200) {
      const json = (await response.json()) as {
        duplicates?: DuplicateMatch[]
        donation?: Record<string, unknown>
        team_id?: string
      }
      if (json.duplicates && json.duplicates.length > 0) {
        setDuplicates(json.duplicates)
        setPendingDonation(json.donation ?? (formData.donation as unknown as Record<string, unknown>))
        setPendingTeamId(json.team_id ?? formData.team_id)
        setShowDuplicates(true)
        return
      }
    }
    setErrors({ base: 'Une erreur est survenue.' })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setErrors({})
    try {
      const response = await sheetFetch('/fundraising/donations', {
        method: 'POST',
        body: { donation: formData.donation, team_id: formData.team_id },
      })
      await handleResponse(response)
    } catch {
      setErrors({ base: 'Impossible de creer le don.' })
    } finally {
      setProcessing(false)
    }
  }

  // Force-create ("Creer quand meme") re-enters the same JSON contract via the
  // dialog. A 201 closes everything; a 422 surfaces inline errors here rather
  // than silently closing.
  function handleForceCreated() {
    setShowDuplicates(false)
    handleCreated()
  }

  function handleForceErrors(errs: Record<string, string>) {
    setShowDuplicates(false)
    setErrors(errs)
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Nouveau don</SheetTitle>
            <SheetDescription>
              Enregistrez un nouveau don dans le systeme.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-4 p-4">
            {errors.base && (
              <p className="text-sm text-destructive">{errors.base}</p>
            )}

            {/* Owning team (multi-team users only) */}
            <TeamPicker
              value={formData.team_id}
              onChange={(val) => setFormData((prev) => ({ ...prev, team_id: val }))}
              error={errors.team_id}
              label="Equipe"
              placeholder="Choisir une equipe"
            />

            {/* Donor */}
            <div className="space-y-2">
              <Label>Donateur *</Label>
              <DonorCombobox
                value={selectedDonor}
                onChange={handleDonorChange}
                error={errors.donor || errors.donor_type}
                locked={lockDonor}
              />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="don_amount">Montant (TND) *</Label>
              <Input
                id="don_amount"
                type="number"
                min="0.001"
                step="0.001"
                value={formData.donation.amount}
                onChange={(e) => updateDonation({ amount: e.target.value })}
                placeholder="0.000"
                required
              />
              {errors.amount && (
                <p className="text-sm text-destructive">{errors.amount}</p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="don_date">Date du don *</Label>
              <Input
                id="don_date"
                type="date"
                value={formData.donation.donation_date}
                onChange={(e) => updateDonation({ donation_date: e.target.value })}
                required
              />
              {errors.donation_date && (
                <p className="text-sm text-destructive">{errors.donation_date}</p>
              )}
            </div>

            {/* Campaign */}
            <div className="space-y-2">
              <Label>Campagne</Label>
              <Select
                value={formData.donation.campaign_id}
                onValueChange={(val) => updateDonation({ campaign_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une campagne" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.campaign && (
                <p className="text-sm text-destructive">{errors.campaign}</p>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Mode de paiement *</Label>
              <Select
                value={formData.donation.payment_method_id}
                onValueChange={(val) => updateDonation({ payment_method_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un mode de paiement" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((pm) => (
                    <SelectItem key={pm.id} value={String(pm.id)}>
                      {pm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.payment_method && (
                <p className="text-sm text-destructive">{errors.payment_method}</p>
              )}
            </div>

            {/* Nature */}
            <div className="space-y-2">
              <Label>Nature du don *</Label>
              <Select
                value={formData.donation.nature_id}
                onValueChange={(val) => updateDonation({ nature_id: val })}
                disabled={noActiveNatures}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une nature" />
                </SelectTrigger>
                <SelectContent>
                  {natures.map((n) => (
                    <SelectItem key={n.id} value={String(n.id)}>
                      {n.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {noActiveNatures && (
                <p className="text-sm text-muted-foreground">
                  Aucune nature active — un administrateur doit en creer une.
                </p>
              )}
              {errors.nature && (
                <p className="text-sm text-destructive">{errors.nature}</p>
              )}
            </div>

            {/* Transaction reference */}
            <div className="space-y-2">
              <Label htmlFor="don_ref">
                Reference de transaction{referenceRequired ? ' *' : ''}
              </Label>
              <Input
                id="don_ref"
                value={formData.donation.transaction_reference}
                onChange={(e) => updateDonation({ transaction_reference: e.target.value })}
                placeholder="REF-..."
              />
              {errors.transaction_reference && (
                <p className="text-sm text-destructive">{errors.transaction_reference}</p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="don_notes">Notes</Label>
              <Input
                id="don_notes"
                value={formData.donation.notes}
                onChange={(e) => updateDonation({ notes: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={processing || noActiveNatures} className="flex-1">
                {processing ? 'Creation...' : 'Creer'}
              </Button>
              <Button type="button" variant="outline" onClick={handleClose}>
                Annuler
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Duplicate warning dialog */}
      {duplicates.length > 0 && (
        <DuplicateWarningDialog
          open={showDuplicates}
          onOpenChange={setShowDuplicates}
          duplicates={duplicates}
          donationParams={pendingDonation}
          teamId={pendingTeamId}
          onCreated={handleForceCreated}
          onErrors={handleForceErrors}
        />
      )}
    </>
  )
}
